import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TrackingService, TrackingView, TrackingEventView, TrackingTone } from '../api';

// ============================================================================
// Nyomkövetés panel — a rendelés oldalsávjában (Pénzügyek alatt, Üzenőfal
// fölött) és a support taskban a kapcsolt rendelés alatt. Ugyanaz a komponens,
// a `compact` bemenet szűkíti a support-változatra.
// ============================================================================

interface EventRow {
  event: TrackingEventView;
  timeLabel: string;
  alert: boolean;
}

interface ShipmentRow {
  view: TrackingView;
  events: EventRow[];
  hiddenEvents: number;
  transitLabel: string | null;
  updatedLabel: string | null;
  open: boolean;
}

@Component({
  selector: 'app-tracking-panel',
  templateUrl: './tracking-panel.component.html',
  styleUrl: './tracking-panel.component.scss'
})
export class TrackingPanelComponent implements OnChanges {
  /** melyik rendelés küldeményeit mutatjuk */
  @Input() orderId?: number | null;
  /** support-változat: kevesebb esemény, nincs saját fejléc */
  @Input() compact = false;

  loading = false;
  loadFailed = false;
  shipments: ShipmentRow[] = [];
  /** a kártya nyitva van-e (a rendelés oldalon egy kártya fogja az összes küldeményt) */
  open = false;
  /** összesítő a becsukott fejléchez: a legsúlyosabb állapot, több csomagnál a darabszám */
  summaryLabel = '';
  summaryTone: TrackingTone = 'grey';
  countLabel = '';
  /** a compact nézetben kinyitott teljes előzmény */
  expandedAll = false;
  /** kézi frissítés folyamatban */
  refreshing = false;

  // Kézi kódrögzítés — nem ShippyPro-címkés feladásnál (UPS, Foxpost, Spring
  // papíron) itt kerül a kód a rendelésre és a nyomkövetésbe.
  manualOpen = false;
  manualCode = '';
  manualCarrier = '';
  manualSaving = false;
  manualError: string | null = null;
  readonly carrierOptions: { value: string; label: string }[] = [
    { value: '', label: 'Futár: automatikus felismerés' },
    { value: 'Spring GDS', label: 'Spring GDS' },
    { value: 'UPS', label: 'UPS' },
    { value: 'Foxpost', label: 'Foxpost' },
    { value: 'GLS', label: 'GLS' },
    { value: 'DPD', label: 'DPD' },
    { value: 'Magyar Posta', label: 'Magyar Posta' },
    { value: 'DHL', label: 'DHL' },
    { value: 'FedEx', label: 'FedEx' },
  ];

  private static readonly COMPACT_EVENT_LIMIT = 3;

  constructor(private tracking: TrackingService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderId']) {
      this.load();
    }
  }

  load(): void {
    const id = Number(this.orderId);
    if (!Number.isInteger(id) || id <= 0) {
      this.shipments = [];
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    this.tracking.getForOrder(id).subscribe({
      next: (views) => {
        this.shipments = (views || []).map((view) => this.build(view));
        this.summarize();
        this.loading = false;
      },
      error: () => {
        this.shipments = [];
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  private build(view: TrackingView): ShipmentRow {
    const limit = this.compact && !this.expandedAll
      ? TrackingPanelComponent.COMPACT_EVENT_LIMIT
      : view.events.length;
    const shown = view.events.slice(0, limit);

    return {
      view,
      events: shown.map((event, index) => ({
        event,
        timeLabel: this.formatEventTime(event.eventAt),
        // A legfrissebb esemény kap kiemelt pontot; hiba esetén pirosat.
        alert: index === 0 && this.isProblem(event.statusCode),
      })),
      hiddenEvents: Math.max(0, view.events.length - shown.length),
      transitLabel: this.transitLabel(view),
      updatedLabel: this.relativeLabel(view.lastPolledAt || view.lastEventAt),
      // Több küldeménynél mindegyik kinyitva jelenik meg a kártyán belül; a
      // kártya maga csukva indul, mint a Pénzügyek.
      open: true,
    };
  }

  private isProblem(statusCode: number | null): boolean {
    return statusCode === 4000 || statusCode === 5000;
  }

  toggle(): void {
    this.open = !this.open;
  }

  /**
   * Kézi frissítés a ShippyPro-ból. A support változatban nincs gomb (a task
   * ne nyúljon el), a rendelés oldalon viszont ez a leggyakoribb művelet:
   * „mi van most a csomaggal?".
   */
  refresh(event?: Event): void {
    event?.stopPropagation();
    const id = Number(this.orderId);
    if (this.refreshing || !Number.isInteger(id) || id <= 0) {
      return;
    }
    this.refreshing = true;
    this.tracking.refreshOrder(id).subscribe({
      next: (views) => {
        this.shipments = (views || []).map((view) => this.build(view));
        this.summarize();
        this.refreshing = false;
      },
      error: () => {
        this.refreshing = false;
        this.loadFailed = true;
      },
    });
  }

  /**
   * A becsukott fejléc a LEGSÚLYOSABB állapotot mutatja, nem az elsőt — ha
   * bármelyik csomag elakadt, azt kell látni becsukva is. Több küldeménynél a
   * darabszám is kiíródik.
   */
  private summarize(): void {
    if (!this.shipments.length) {
      this.summaryLabel = 'Nincs küldeménykód';
      this.summaryTone = 'grey';
      this.countLabel = '';
      return;
    }
    // Súlyossági sorrend: fennakadás / sikertelen > úton > kiszállítás alatt >
    // információ > kézbesítve. A „mindent kézbesítettek" a legjobb hír.
    const severity: Record<string, number> = { red: 5, blue: 3, yellow: 2, grey: 1, green: 0 };
    const worst = this.shipments.reduce((acc, row) =>
      (severity[row.view.tone] ?? 0) > (severity[acc.view.tone] ?? 0) ? row : acc,
      this.shipments[0]);

    const allDelivered = this.shipments.every((row) => row.view.statusCode === 6000);
    this.summaryTone = allDelivered ? 'green' : worst.view.tone;
    this.summaryLabel = allDelivered && this.shipments.length > 1
      ? 'Mind kézbesítve'
      : worst.view.statusLabel;
    this.countLabel = this.shipments.length > 1
      ? `${this.shipments.length} küldemény`
      : '';
  }

  toggleManual(event?: Event): void {
    event?.stopPropagation();
    this.manualOpen = !this.manualOpen;
    this.manualError = null;
    if (!this.manualOpen) {
      this.manualCode = '';
      this.manualCarrier = '';
    }
  }

  saveManual(): void {
    const id = Number(this.orderId);
    const code = this.manualCode.trim();
    if (this.manualSaving || !code || !Number.isInteger(id) || id <= 0) {
      return;
    }
    this.manualSaving = true;
    this.manualError = null;
    this.tracking.setManualCode(id, code, this.manualCarrier || null).subscribe({
      next: () => {
        this.manualSaving = false;
        this.manualOpen = false;
        this.manualCode = '';
        this.manualCarrier = '';
        this.open = true;
        this.load();
      },
      error: (err) => {
        this.manualSaving = false;
        this.manualError = err?.error?.message || 'A küldeménykód rögzítése nem sikerült.';
      },
    });
  }

  /** Van-e legalább egy elakadt küldemény — a fejlécen pont jelzi. */
  get hasStalled(): boolean {
    return this.shipments.some((row) => row.view.stalled);
  }

  /** compact nézetben a „teljes előzmény" kinyitása */
  showAll(): void {
    this.expandedAll = true;
    this.shipments = this.shipments.map((row) => this.build(row.view));
  }

  toneClass(tone: string): string {
    return 'trk-pill--' + (tone || 'grey');
  }

  /** „feladva → érkezett" vagy „x napja úton” */
  private transitLabel(view: TrackingView): string | null {
    if (!view.shippedAt) {
      return null;
    }
    const shipped = this.formatDate(view.shippedAt);
    if (view.deliveredAt) {
      const days = view.daysInTransit;
      return `${shipped} → ${this.formatDate(view.deliveredAt)}`
        + (days !== null ? ` (${days} nap)` : '');
    }
    const days = view.daysInTransit;
    return days !== null ? `${shipped} · ${days} napja úton` : shipped;
  }

  private formatDate(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  }

  /** Ma / tegnap / dátum + óra — a support így gyorsabban olvassa. */
  private formatEventTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) { return ''; }
    const time = date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
    if (dayDiff === 0) { return `ma ${time}`; }
    if (dayDiff === 1) { return `tegnap ${time}`; }
    return `${date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })} ${time}`;
  }

  private relativeLabel(iso: string | null): string | null {
    if (!iso) { return null; }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) { return null; }
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) { return 'az imént'; }
    if (minutes < 60) { return `${minutes} perce`; }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) { return `${hours} órája`; }
    return `${Math.floor(hours / 24)} napja`;
  }
}
